import React, { FC, useCallback, useState, useContext, useEffect, ChangeEvent, ReactNode } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import makeStyles from '@mui/styles/makeStyles';

import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import InputLabel from '@mui/material/InputLabel'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import FormHelperText from '@mui/material/FormHelperText'
import FormControl from '@mui/material/FormControl'
import Select from '@mui/material/Select'

import TinyButton from '../widgets/TinyButton'
import MagicTagIcon from './MagicTagIcon'

import decorator from './decorators'

import {
  EditorState,
  SelectionState,
} from 'draft-js'

import EditorContext from './EditorContext'

import {
  ITextEditor_EditorUpdate,
} from '@kaidam/fc-shared/src/types/texteditor'

import Window from '../dialog/Window'

import {
  IconComponentType,
} from '../../types/app'

import {
  IData_MagicTag_Summary,
  IData_MagicTag_Type,
  IData_MagicTag_Instance,
} from '@kaidam/fc-shared/src/types/schema/magic_tag'

import {
  MAGICTAG_ROLE_CRM,
  MAGICTAG_ROLE_ORG,
  MAGICTAG_TYPE_CRM,
  MAGICTAG_TYPE_ORG,
} from '@kaidam/fc-shared/src/constants'

import ToolbarButton from './ToolbarButton'

import projectSelectors from '../../store/selectors/project'
import crmSelectors from '../../store/selectors/crm'
import rbacSelectors from '../../store/selectors/rbac'
import crmActions from '../../store/modules/crm'
import rbacActions from '../../store/modules/rbac'

import {
  injectMagicTag,
} from './utils'

import icons from '../../icons'

const AddIcon = icons.add

const useStyles = makeStyles(theme => ({
  formControl: {
    width: '100%',
  },
  select: {
    width: '100%',
  },
  linkContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    '& .input': {
      flexGrow: 1,
    },
    '& .left': {
      flexGrow: 0,
      marginRight: theme.spacing(1),
    },
    '& .right': {
      flexGrow: 0,
      marginLeft: theme.spacing(1),
    },
  },
}))

interface ToolbarMagicRowProps {
  disabled?: boolean,
  icon: ReactNode,
  onSelect: {
    (): void,
  }
}

const ToolbarMagicRow: FC<React.PropsWithChildren<ToolbarMagicRowProps>> = ({
  disabled = false,
  icon,
  onSelect,
  children,
}) => {
  const classes = useStyles()

  return (
    <div className={ classes.linkContainer }>
      <div className="left">
        { icon }
      </div>
      <div className="input">
        { children }
      </div>
      <div className="right">
        <TinyButton
          color="primary"
          variant="contained"
          startIcon={
            <AddIcon />
          }
          disabled={ disabled }
          onClick={ onSelect }
        >
          Add
        </TinyButton>
      </div>
    </div>
    
  )
}

interface ToolbarMagicTagSelectProps {
  title: string,
  helperText: string,
  icon: ReactNode,
  type: IData_MagicTag_Type,
  options: IData_MagicTag_Summary[],
  onSelect: {
    (tag: IData_MagicTag_Instance): void,
  }
}

const ToolbarMagicTagSelect: FC<React.PropsWithChildren<ToolbarMagicTagSelectProps>> = ({
  title,
  helperText,
  icon,
  type,
  options,
  onSelect,
}) => {
  const classes = useStyles()

  const [ field, setField ] = useState('choose')
  const [ defaultValue, setDefaultValue ] = useState('')

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setField(event.target.value as string)
  }

  const handleSelect = () => {
    onSelect({
      type,
      field,
      default: defaultValue,
    })
  }

  return (
    <ToolbarMagicRow
      icon={ icon }
      disabled={ field == 'choose' || !defaultValue }
      onSelect={ handleSelect }
    >
      <Grid container spacing={ 2 }>
        <Grid item xs={ 6 }>
          <FormControl className={classes.formControl}>
            <InputLabel>{ title }</InputLabel>
            <Select
              className={ classes.select }
              value={ field }
              onChange={ handleChange }
            >
              <MenuItem value="choose">
                <em>Choose...</em>
              </MenuItem>
              {
                options.map((option) => (
                  <MenuItem key={ option.name } value={ option.name }>{ option.name } ({ option.count })</MenuItem>
                ))
              }
            </Select>
            <FormHelperText>{ helperText }</FormHelperText>
          </FormControl>
        </Grid>
        <Grid item xs={ 6 }>
          <TextField
            fullWidth
            label="default value"
            helperText="used if the magic tag is missing"
            value={ defaultValue }
            onChange={ (e: ChangeEvent<HTMLInputElement>) => setDefaultValue(e.target.value as string)}
          />
        </Grid>
      </Grid>
      
    </ToolbarMagicRow>
    
  )
}

interface ToolbarMagicTagLinkProps {
  helperText: string,
  icon: ReactNode,
  tag: IData_MagicTag_Instance,
  onSelect: {
    (tag: IData_MagicTag_Instance): void,
  }
}

const ToolbarMagicTagLink: FC<React.PropsWithChildren<ToolbarMagicTagLinkProps>> = ({
  helperText,
  icon,
  tag,
  onSelect,
}) => {
  const onAdd = () => {
    onSelect(tag)
  }

  return (
    <ToolbarMagicRow
      icon={ icon }
      onSelect={ onAdd }
    >
      <Typography variant="caption">
        { helperText }
      </Typography>
    </ToolbarMagicRow>
  )
}

interface ToolbarButtonMagicTagProps {
  className?: string,
  addExtraCharacters?: string,
  enabled: IData_MagicTag_Type[],
  icon: IconComponentType,
  editorState: EditorState,
  setEditorState: ITextEditor_EditorUpdate,
}

const ToolbarButtonMagicTag: FC<React.PropsWithChildren<ToolbarButtonMagicTagProps>> = ({
  className,
  addExtraCharacters = '',
  enabled,
  icon,
  editorState,
  setEditorState,
  children,
}) => {

  const [ windowOpen, setWindowOpen ] = useState(false)
  const dispatch = useDispatch()
  const userSummary = useSelector(projectSelectors.summary)
  const crmTags = useSelector(crmSelectors.magicTags)
  const orgTags = useSelector(rbacSelectors.magicTags)

  const editorContext = useContext(EditorContext)

  const crmRole = userSummary?.roles[MAGICTAG_ROLE_CRM]
  const rbacRole = userSummary?.roles[MAGICTAG_ROLE_ORG]

  const crmEnabled = crmRole && enabled.indexOf(MAGICTAG_TYPE_CRM) >= 0
  const orgEnabled = rbacRole && enabled.indexOf(MAGICTAG_TYPE_ORG) >= 0

  useEffect(() => {
    if(crmEnabled) {
      dispatch(crmActions.loadMagicTags())
    }
    if(orgEnabled) {
      dispatch(rbacActions.loadMagicTags())
    }
  }, [])

  const onCancel = useCallback(() => {
    setWindowOpen(false)
    setTimeout(() => {
      editorContext.focus()
    }, 0)
  }, [])

  const onAdd = useCallback((tag: IData_MagicTag_Instance, replace = false) => {
    const useEditorState = replace ? EditorState.createEmpty(decorator) : editorState
    const selectionState = replace ? SelectionState.createEmpty(useEditorState.getCurrentContent().getFirstBlock().getKey()) : editorState.getSelection()
    const newContent = injectMagicTag(
      useEditorState.getCurrentContent(),
      selectionState,
      tag,
      replace ? '' : addExtraCharacters,
    )
    setEditorState(EditorState.push(useEditorState, newContent, 'insert-characters'))
    onCancel()
  }, [
    editorState,
    addExtraCharacters,
  ])

  const onAddReplace = useCallback((tag: IData_MagicTag_Instance) => {
    onAdd(tag, true)
  }, [
    onAdd,
  ])

  if(!crmEnabled && !orgEnabled) {
    return null
  }

  return (
    <>
      <ToolbarButton
        title="magic tag"
        className={ className }
        icon={ icon }
        active={ false }
        onClick={ () => setWindowOpen(true) }
      />
      {
        windowOpen && (
          <Window
            open
            size="md"
            withCancel
            smallButtons
            cancelTitle="Cancel"
            onCancel={ onCancel }
          >
            {
              orgEnabled && (
                <ToolbarMagicTagSelect
                  title="ORG Magic Tag"
                  helperText="Choose a magic tag for an org"
                  icon={
                    <MagicTagIcon type={ MAGICTAG_TYPE_ORG } />
                  }
                  type={ MAGICTAG_TYPE_ORG }
                  options={ orgTags }
                  onSelect={ onAdd }
                />
              )
            }
            {
              crmEnabled && (
                <ToolbarMagicTagSelect
                  title="CRM Magic Tag"
                  helperText="Choose a magic tag for a CRM contact"
                  icon={
                    <MagicTagIcon type={ MAGICTAG_TYPE_CRM } />
                  }
                  type={ MAGICTAG_TYPE_CRM }
                  options={ crmTags }
                  onSelect={ onAdd }
                />
              )
            }
            {/* {
              astonishUnsubscribeEnabled && (
                <ToolbarMagicTagLink
                  helperText="Astonish Unsubscribe URL"
                  icon={
                    <MagicTagIcon type={ MAGICTAG_TYPE_ASTONISH_UNSUBSCRIBE } />
                  }
                  tag={{
                    type: MAGICTAG_TYPE_ASTONISH_UNSUBSCRIBE,
                    field: 'url',
                    text: 'unsubscribe url',
                  }}
                  onSelect={ onAddReplace }
                />
              )
            }
            {
              astonishOpenInBrowserEnabled && (
                <ToolbarMagicTagLink
                  helperText="Astonish Open in Browser URL"
                  icon={
                    <MagicTagIcon type={ MAGICTAG_TYPE_ASTONISH_OPEN_BROWSER } />
                  }
                  tag={{
                    type: MAGICTAG_TYPE_ASTONISH_OPEN_BROWSER,
                    field: 'url',
                    text: 'open in browser url',
                  }}
                  onSelect={ onAddReplace }
                />
              )
            } */}
          </Window>
        )
      }
      {
        children
      }
    </>
  )
}

export default ToolbarButtonMagicTag